import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const BASE_URL = "https://noleggiofurgoni.it";

export default function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  schema,
  noindex = false
}) {
  const fullTitle = title ? `${title} | NoleggioFurgoni.it` : "Noleggio Furgoni Italia | Confronta Prezzi e Prenota Online";
  const fullDescription = description || "Noleggio furgoni in tutta Italia. Confronta prezzi, leggi recensioni e prenota online il furgone perfetto per traslochi, trasporti e viaggi.";
  const fullImage = image || `${BASE_URL}/og-image.jpg`;
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  // Fallback: directly set document.title for React 19 compatibility
  useEffect(() => {
    document.title = fullTitle;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', fullDescription);
    }
  }, [fullTitle, fullDescription]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      
      {/* Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />
      
      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

// Schema generators
export const generateVehicleSchema = (vehicle, company) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": `${vehicle.brand} ${vehicle.model}`,
  "description": vehicle.description || `Furgone ${vehicle.brand} ${vehicle.model} disponibile per noleggio`,
  "image": vehicle.images?.[0] || `${BASE_URL}/default-vehicle.jpg`,
  "brand": {
    "@type": "Brand",
    "name": vehicle.brand
  },
  "offers": {
    "@type": "Offer",
    "price": vehicle.daily_rate,
    "priceCurrency": "EUR",
    "availability": vehicle.is_available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    "seller": company ? {
      "@type": "Organization",
      "name": company.name,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": company.city,
        "addressRegion": company.province,
        "addressCountry": "IT"
      }
    } : undefined
  },
  "additionalProperty": [
    { "@type": "PropertyValue", "name": "Anno", "value": vehicle.year },
    { "@type": "PropertyValue", "name": "Motorizzazione", "value": vehicle.engine_type },
    { "@type": "PropertyValue", "name": "Volume Carico", "value": `${vehicle.cargo_volume_m3} m³` },
    { "@type": "PropertyValue", "name": "Lunghezza", "value": `${vehicle.length_m} m` }
  ]
});

export const generateCatalogSchema = (vehicles) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Catalogo Furgoni",
  "description": "Lista dei furgoni disponibili per il noleggio",
  "numberOfItems": vehicles.length,
  "itemListElement": vehicles.slice(0, 10).map((vehicle, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Product",
      "name": `${vehicle.brand} ${vehicle.model}`,
      "url": `${BASE_URL}/veicolo/${vehicle.vehicle_id}`
    }
  }))
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url ? `${BASE_URL}${item.url}` : undefined
  }))
});
